import type { AdminAgentWalletTransactionRow, AdminAgentPaymentTransactionRow } from '../../../types';
import { formatInTimezone } from '@kioskgaming/ui';

export const AGENT_TX_PAGE_SIZE = 10;

export function formatAgentTxDate(
  iso?: string | null,
  timezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone,
): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : formatInTimezone(d, timezone, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
}

const WALLET_TX_TYPE_LABELS: Record<string, string> = {
  deposit: 'Deposit',
  withdrawal: 'Withdrawal',
  transfer: 'Transfer',
  bonus: 'Bonus',
  adjustment: 'Balance correction',
  reservation: 'Reservation',
  reservation_release: 'Reservation release',
  credit: 'Credit',
  debit: 'Debit',
};

export function formatWalletLedgerType(type: string | undefined | null): string {
  if (!type) return '—';
  return WALLET_TX_TYPE_LABELS[type] ?? type.replace(/_/g, ' ');
}

/** Ledger amounts are CDN credits — readable decimals, no currency symbol. */
export function formatCdnLedgerAmount(value: string | number | undefined | null): string {
  if (value === undefined || value === null || value === '') return '—';
  const n = typeof value === 'string' ? parseFloat(value) : value;
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 8,
  }).format(n);
}

export function getSignedLedgerDelta(row: AdminAgentWalletTransactionRow): {
  signed: number;
  displayAbs: string;
} {
  const before = parseFloat(String(row.balanceBefore ?? 0));
  const after = parseFloat(String(row.balanceAfter ?? 0));
  if (!Number.isFinite(before) || !Number.isFinite(after)) {
    const amt = parseFloat(String(row.amount ?? 0));
    if (!Number.isFinite(amt)) return { signed: 0, displayAbs: '—' };
    return { signed: amt, displayAbs: formatCdnLedgerAmount(amt) };
  }
  const signed = after - before;
  return { signed, displayAbs: formatCdnLedgerAmount(Math.abs(signed)) };
}

export function formatReferenceCell(row: AdminAgentWalletTransactionRow): { text: string; title: string } | null {
  const id = row.referenceId?.trim();
  if (!id) return null;
  const rt = (row.referenceType || '').trim();
  const title = rt ? `${rt}:${id}` : id;
  const shortId = id.length > 14 ? `${id.slice(0, 8)}…${id.slice(-6)}` : id;
  const text = rt ? `${rt}: ${shortId}` : shortId;
  return { text, title };
}

/**
 * Agent CDN purchase: `amount` = charged by cost rate; `metadata.creditAmount` = credits received.
 * Admin cash: both are 1:1; creditAmount still set in metadata.
 */
export function getAgentPaymentPaidAndCredits(row: AdminAgentPaymentTransactionRow): {
  paid: number;
  creditsReceived: number;
} | null {
  const paid = parseFloat(String(row.amount ?? ''));
  if (!Number.isFinite(paid)) return null;
  const meta = row.metadata as { creditAmount?: number | string } | undefined;
  const raw = meta?.creditAmount;
  if (raw === undefined || raw === null || raw === '') {
    return { paid, creditsReceived: paid };
  }
  const creditsReceived = typeof raw === 'number' ? raw : parseFloat(String(raw));
  if (!Number.isFinite(creditsReceived)) return { paid, creditsReceived: paid };
  return { paid, creditsReceived };
}

export function formatAgentPaymentMoney(amount: number, currency?: string | null): string {
  if (!Number.isFinite(amount)) return '—';
  const code = (currency || 'USD').toString().trim().toUpperCase();
  if (code.length === 3) {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: code,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch {
      /* fall through */
    }
  }
  return `${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 8 })} ${currency || ''}`.trim();
}

/** CDN credits — no $ symbol (credits are not fiat). */
export function formatAgentCreditsReceived(amount: number): string {
  if (!Number.isFinite(amount)) return '—';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 8,
  }).format(amount);
}
